import { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import { attendanceReport, listAttendanceStaff } from "../../api/attendanceService";
import { listDepartments } from "../../api/departmentService";
import { fetchAndDownload } from "../../utils/exportCsv";
import { nowIst, todayKeyIst } from "../../utils/formatters";
import { showApiError } from "../../utils/toast";

/**
 * The report suite: one picker, a shared filter bar, a generic result table
 * driven by the API's header/rows, and CSV export of exactly what's shown.
 */
const REPORTS = [
  { key: "daily", label: "Daily attendance", filters: ["date"] },
  { key: "muster", label: "Monthly muster roll", filters: ["month"] },
  { key: "late", label: "Late arrivals", filters: ["range", "user", "department"] },
  { key: "absenteeism", label: "Absenteeism", filters: ["range", "user", "department"] },
  { key: "shortfall", label: "Hours shortfall", filters: ["range", "user", "department"] },
  { key: "hours-summary", label: "Work hours summary", filters: ["range", "user", "department"] },
  { key: "missing-checkout", label: "Missing checkouts", filters: ["range", "user", "department"] },
  { key: "geofence-exceptions", label: "Geofence exceptions", filters: ["range", "user", "department"] },
  { key: "leave-balances", label: "Leave balances", filters: ["year", "user", "department"] },
  { key: "leave-log", label: "Leave applications log", filters: ["range", "user", "department"] },
];

export default function AttendanceReportsPage() {
  const [report, setReport] = useState("daily");
  const [date, setDate] = useState(todayKeyIst());
  const [month, setMonth] = useState(nowIst().format("YYYY-MM"));
  const [from, setFrom] = useState(nowIst().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(todayKeyIst());
  const [year, setYear] = useState(nowIst().year());
  const [userId, setUserId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [exporting, setExporting] = useState(false);

  const staff = useApi(() => listAttendanceStaff());
  const departments = useApi(() => listDepartments({ per_page: 100 }));

  const def = REPORTS.find((r) => r.key === report);
  const params = buildParams(def, { date, month, from, to, year, userId, departmentId });

  const result = useApi(
    () => attendanceReport(report, params),
    [report, JSON.stringify(params)],
  );

  const data = result.data?.data;
  const header = data?.header || [];
  const rows = data?.rows || [];

  const staffOptions = (staff.data?.data || []).map((u) => ({
    id: u.id,
    label: u.name,
    hint: u.role === "admin" ? "Admin" : u.department || "Employee",
  }));
  const deptRows = departments.data?.data?.data || departments.data?.data || [];
  const deptOptions = (Array.isArray(deptRows) ? deptRows : []).map((d) => ({ id: d.id, label: d.name }));

  const exportCsv = async () => {
    setExporting(true);
    try {
      await fetchAndDownload(`/attendance/reports/${report}`, { ...params, format: "csv" }, `attendance_${report}`);
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Attendance Reports</h1>
          <div className="page-sub">Every report is filterable and exports exactly what you see.</div>
        </div>
        <div className="page-actions">
          <Link className="btn btn-secondary" to="/admin/attendance">
            <Icon name="chevron-left" size={14} /> Register
          </Link>
          <button className="btn btn-primary" onClick={exportCsv} disabled={exporting || result.loading}>
            <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {/* Filter bar */}
          <div className="row gap-md" style={{ flexWrap: "wrap", marginBottom: 14, alignItems: "flex-end" }}>
            <div className="field" style={{ width: 220, marginBottom: 0 }}>
              <label className="field-label">Report</label>
              <select className="select" value={report} onChange={(e) => setReport(e.target.value)}>
                {REPORTS.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>

            {def.filters.includes("date") && (
              <div className="field" style={{ width: 160, marginBottom: 0 }}>
                <label className="field-label">Date</label>
                <input type="date" className="input" max={todayKeyIst()} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
              </div>
            )}
            {def.filters.includes("month") && (
              <div className="field" style={{ width: 160, marginBottom: 0 }}>
                <label className="field-label">Month</label>
                <input type="month" className="input" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
              </div>
            )}
            {def.filters.includes("range") && (
              <>
                <div className="field" style={{ width: 160, marginBottom: 0 }}>
                  <label className="field-label">From</label>
                  <input type="date" className="input" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
                </div>
                <div className="field" style={{ width: 160, marginBottom: 0 }}>
                  <label className="field-label">To</label>
                  <input type="date" className="input" min={from} max={todayKeyIst()} value={to} onChange={(e) => e.target.value && setTo(e.target.value)} />
                </div>
              </>
            )}
            {def.filters.includes("year") && (
              <div className="field" style={{ width: 120, marginBottom: 0 }}>
                <label className="field-label">Year</label>
                <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {[year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}
            {def.filters.includes("user") && (
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label">Person</label>
                <SearchableSelect
                  value={userId}
                  onChange={(v) => setUserId(v || "")}
                  options={[{ id: "", label: "Everyone" }, ...staffOptions]}
                  placeholder="Everyone"
                  width={200}
                />
              </div>
            )}
            {def.filters.includes("department") && deptOptions.length > 0 && (
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label">Department</label>
                <SearchableSelect
                  value={departmentId}
                  onChange={(v) => setDepartmentId(v || "")}
                  options={[{ id: "", label: "All departments" }, ...deptOptions]}
                  placeholder="All departments"
                  width={190}
                />
              </div>
            )}
          </div>

          {/* Result */}
          {result.loading ? (
            <LoadingBlock rows={6} />
          ) : rows.length === 0 ? (
            <EmptyState icon="file-text" title="No rows" hint="Nothing matches this report's filters (or the day-end sweep hasn't covered the range yet)." />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ whiteSpace: "nowrap" }}>
                <thead>
                  <tr>
                    {header.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((v, j) => (
                        <td key={j} className={typeof v === "number" ? "t-num" : undefined}>
                          {formatCell(v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function buildParams(def, { date, month, from, to, year, userId, departmentId }) {
  const p = {};
  if (def.filters.includes("date")) p.date = date;
  if (def.filters.includes("month")) p.month = month;
  if (def.filters.includes("range")) {
    p.from = from;
    p.to = to;
  }
  if (def.filters.includes("year")) p.year = year;
  if (def.filters.includes("user") && userId) p.user_id = userId;
  if (def.filters.includes("department") && departmentId) p.department_id = departmentId;
  return p;
}

function formatCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true) return "yes";
  if (v === false) return "no";
  return String(v);
}
